import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, OnDestroy, SimpleChanges
} from '@angular/core';
import { BalanceRound } from '../../../interfaces/balance-it.interface';

interface Pt { x: number; y: number; }

/**
 * Balance It play surface — a balance scale. The left pan holds the fixed
 * measure (`fromValue fromUnit`); the right pan holds `n` of the smaller unit,
 * set with the slider (or the shell's ± buttons). The beam tilts by the
 * relative difference and levels when `n === answer` (= fromValue × factor);
 * settling there wins.
 *
 * All quantities are integers so the comparison is exact.
 */
@Component({
  selector: 'app-balance-it-stage',
  templateUrl: './balance-it-stage.component.html',
  styleUrls: ['./balance-it-stage.component.scss']
})
export class BalanceItStageComponent implements OnChanges, OnDestroy {
  @Input() round: BalanceRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() changed = new EventEmitter<void>();   // n changed but not yet solved

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 560;

  // Scale geometry.
  readonly PX = 500;      // pivot x
  readonly PY = 150;      // pivot y
  readonly BB = 236;      // beam half-length
  readonly CHAIN = 92;    // chain length (beam end → pan)
  readonly MAX_TILT = 0.24;   // radians

  // Slider geometry.
  readonly SX0 = 250; readonly SX1 = 750; readonly SLY = 508;

  /** Current amount on the right pan, in `toUnit`. */
  n = 0;
  solvedState = false;

  private dragging = false;
  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }
  ngOnDestroy(): void { this.clearTimers(); }

  private reset(): void {
    this.clearTimers();
    this.n = 0;
    this.solvedState = false;
    this.dragging = false;
  }

  // ── Round shorthands ───────────────────────────────────────────────
  get answer(): number { return this.round?.answer ?? 0; }
  get step(): number { return this.round?.step ?? 1; }
  get gaugeMax(): number { return this.round?.gaugeMax ?? 10; }
  get fromLabel(): string { return `${this.round?.fromValue ?? 0} ${this.round?.fromUnit ?? ''}`; }
  get toLabel(): string { return `${this.n} ${this.round?.toUnit ?? ''}`; }

  // ── Tilt + beam / pan geometry ─────────────────────────────────────
  /** >0 tilts the right (answer) side down — i.e. n is too heavy. */
  get tiltRad(): number {
    if (this.solvedState || !this.answer) return 0;
    const ratio = (this.n - this.answer) / this.answer;
    return Math.max(-this.MAX_TILT, Math.min(this.MAX_TILT, ratio * 0.6));
  }
  get tiltDeg(): number { return this.tiltRad * 180 / Math.PI; }
  get level(): boolean { return this.n === this.answer; }

  private end(sign: number): Pt {
    const t = this.tiltRad;
    return { x: this.PX + sign * this.BB * Math.cos(t), y: this.PY + sign * this.BB * Math.sin(t) };
  }
  get beamLeft(): Pt { return this.end(-1); }
  get beamRight(): Pt { return this.end(1); }
  get panLeft(): Pt { const e = this.beamLeft; return { x: e.x, y: e.y + this.CHAIN }; }
  get panRight(): Pt { const e = this.beamRight; return { x: e.x, y: e.y + this.CHAIN }; }

  // ── Slider ─────────────────────────────────────────────────────────
  get knobX(): number { return this.SX0 + (this.n / this.gaugeMax) * (this.SX1 - this.SX0); }

  onDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    this.dragging = true;
    this.updateFromEvent(evt);
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }
  onMove(evt: PointerEvent): void { if (this.dragging && !this.solvedState) this.updateFromEvent(evt); }
  onUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }
  private updateFromEvent(evt: PointerEvent): void {
    const x = this.toSvgX(evt);
    const raw = ((x - this.SX0) / (this.SX1 - this.SX0)) * this.gaugeMax;
    this.setN(Math.round(raw / this.step) * this.step);
  }

  /** Set n (clamped/snapped). Wins the instant the scale is exactly level. */
  setN(v: number): void {
    this.n = Math.max(0, Math.min(this.gaugeMax, v));
    if (!this.solvedState && this.n === this.answer && this.n > 0) {
      this.solvedState = true;
      this.dragging = false;   // lock the knob at the balance point
      this.solved.emit();
    } else {
      this.changed.emit();
    }
  }
  nudge(dir: number): void {
    if (!this.interactive || this.solvedState) return;
    this.setN(this.n + dir * this.step);
  }

  private toSvgX(evt: PointerEvent): number {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return 0;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    return pt.matrixTransform(ctm.inverse()).x;
  }
  private clearTimers(): void { this.timers.forEach(t => clearTimeout(t)); this.timers = []; }
}
